import { onBeforeUnmount, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { eventMatchesBinding, useHotkeys } from '~/lib/settings/hotkeys'
import { useQuickSwitcher } from '~/composables/useQuickSwitcher'
import { useCreateChannelRequest } from '~/composables/useCreateChannelRequest'
import { useGroupsStore } from '~/stores/groups'
import { useMessagesStore } from '~/stores/messages'
import { liveKitManager } from '~/lib/livekit/manager'
import { resetZoom, restoreZoom, zoomIn, zoomOut } from '~/lib/zoom'

// The app-wide shortcuts (lib/settings/hotkeys.ts, scope `global`) that don't belong to one
// screen: navigation, read state, voice, zoom. Installed once, in app.vue. Window listener in the
// bubble phase, registered after every page/component listener: anything a closer handler already
// took (`defaultPrevented` — the composer, the search field, an open menu) never gets here, which
// is what makes Escape mark the channel read only when there's nothing left to cancel.
// (Search, the sidebar and the composer's own keys are handled where they live.)

function isOverlayOpen() {
  return !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]')
}

export function useGlobalHotkeys() {
  const hotkeys = useHotkeys()
  const router = useRouter()
  const route = useRoute()
  const quickSwitcher = useQuickSwitcher()
  const createChannel = useCreateChannelRequest()
  const groupsStore = useGroupsStore()
  const messagesStore = useMessagesStore()

  function markChannelRead(channelId: string) {
    groupsStore.clearChannelUnread(channelId)
    const newest = messagesStore.messagesByChannel[channelId]?.filter((m) => m.status !== 'sending' && m.status !== 'failed').at(-1)
    if (newest) groupsStore.setLastReadMessage(channelId, newest.id)
  }

  function markAllRead() {
    for (const channelId of Object.keys(groupsStore.unreadChannels)) markChannelRead(channelId)
    for (const channelId of Object.keys(messagesStore.messagesByChannel)) {
      const newest = messagesStore.messagesByChannel[channelId]?.at(-1)
      if (newest && newest.status !== 'sending' && newest.status !== 'failed') groupsStore.setLastReadMessage(channelId, newest.id)
    }
  }

  // ── push to talk: unmute while held ─────────────────────────────────────────
  let pushToTalkHeld = false
  function releasePushToTalk() {
    if (!pushToTalkHeld) return
    pushToTalkHeld = false
    if (!liveKitManager.state.isMicMuted) void liveKitManager.toggleMic()
  }

  const handlers: [Parameters<typeof hotkeys.matches>[0], () => void][] = [
    ['nav.quickSwitch', () => quickSwitcher.toggle('all')],
    ['nav.browseChannels', () => quickSwitcher.toggle('channels')],
    ['nav.newChannel', () => { if (route.path.startsWith('/group/')) createChannel.request() }],
    ['nav.settings', () => { if (route.path === '/settings') router.back(); else void router.push('/settings') }],
    ['nav.back', () => router.back()],
    ['nav.forward', () => router.forward()],
    ['nav.home', () => { void router.push('/dashboard') }],
    ['read.markAll', markAllRead],
    ['voice.toggleMic', () => { void liveKitManager.toggleMic() }],
    ['voice.toggleDeafen', () => { void liveKitManager.toggleDeaf() }],
    ['voice.leave', () => { if (groupsStore.activeVoiceChannelId) void groupsStore.disconnectChannel() }],
    ['zoom.in', zoomIn],
    ['zoom.out', zoomOut],
    ['zoom.reset', resetZoom],
  ]

  function onKeydown(event: KeyboardEvent) {
    if (event.defaultPrevented || event.isComposing) return

    if (hotkeys.matches('voice.pushToTalk', event)) {
      event.preventDefault()
      if (!event.repeat && !pushToTalkHeld && groupsStore.activeVoiceChannelId && liveKitManager.state.isMicMuted) {
        pushToTalkHeld = true
        void liveKitManager.toggleMic()
      }
      return
    }

    // Escape with nothing left to close: the current channel is read.
    if (hotkeys.matches('read.markChannel', event)) {
      if (isOverlayOpen()) return
      const channelId = groupsStore.selectedChannelId
      if (channelId && route.path.startsWith('/group/')) markChannelRead(channelId)
      return
    }

    for (const [id, run] of handlers) {
      // ⌘+ is ⌘⇧= on most keyboards — counts as «Увеличить» too.
      const zoomInPlus = id === 'zoom.in' && hotkeys.bindingOf('zoom.in') === 'Mod+=' && eventMatchesBinding(event, 'Mod+Shift+=')
      if (!hotkeys.matches(id, event) && !zoomInPlus) continue
      // Inside a dialog the shortcuts that navigate away stay out of its way.
      if (isOverlayOpen() && id !== 'nav.quickSwitch' && id !== 'nav.browseChannels' && !id.startsWith('zoom.') && !id.startsWith('voice.')) return
      event.preventDefault()
      run()
      return
    }
  }

  function onKeyup(event: KeyboardEvent) {
    if (!pushToTalkHeld) return
    // Letting go of any key of the combination ends it.
    const binding = hotkeys.bindingOf('voice.pushToTalk')
    const parts = binding.split('+')
    const key = parts.at(-1)
    const released = event.code === 'Space' ? 'Space' : event.key
    if (released === key || ['Control', 'Meta', 'Alt', 'Shift'].includes(event.key)) releasePushToTalk()
  }

  onMounted(() => {
    restoreZoom()
    window.addEventListener('keydown', onKeydown)
    window.addEventListener('keyup', onKeyup)
    window.addEventListener('blur', releasePushToTalk)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown)
    window.removeEventListener('keyup', onKeyup)
    window.removeEventListener('blur', releasePushToTalk)
  })
}
